import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { DeleteLoyaltyProgramTier, GetLoyaltyProgramRewards, GetLoyaltyProgramTiers, PostDuplicateLoyaltyProgramTier, PostLoyaltyProgramStep3 } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import axios, { CancelToken } from "axios";
import { ButtonNew as Button } from "components/buttonNew";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import Toggle from "components/form/toggle/Toggle";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";
import { isEqualWith, isNull } from "lodash";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { IReward, ITier } from "redux/reducers/models/loyalty";
import "../loyalty.scss";
import LoyaltyProgramTiersModal from "./LoyaltyProgramTiersModal";

interface IState {
    tiers: Array<ITier & {rewards?: Array<Partial<IReward>>}>;
}

export default function LoyaltyProgramTiersStep2() {
    const {t} = useTranslation();
    const history = useHistory();
    const { state: tierModal, updateModal: updateTierModal, closeModal: closeTierModal } = useModal<{tier: ITier}>({tier: null});
    const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal<{tier: ITier}>({tier: null});
    const { state: duplicateModal, updateModal: updateDuplicateModal, closeModal: closeDuplicateModal } = useModal<{tier: ITier}>({tier: null});
    const dispatch = useAppDispatch();
    const [state, setState] = useState<IState>({
        tiers: null,
    });
    const [tiersBeforeState, setTiersBeforeState] = useState(state?.tiers);


    useEffect(() => {
        const source = axios.CancelToken.source();
        void getTiers(source.token);
        return () => source.cancel();
    }, []);


    async function getTiers(cancelToken?: CancelToken) {
        dispatch(enqueue());
        const [tiersRes, rewardsRes] = await Promise.all([GetLoyaltyProgramTiers(false, cancelToken), GetLoyaltyProgramRewards(false, cancelToken)]);
        dispatch(dequeue());
        if (tiersRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting tiers")));
            return;
        }
        if (rewardsRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }
        const tiers: Array<ITier> = tiersRes?.data;
        const rewards: Array<IReward> = rewardsRes?.data;
        const updatedTiers = tiers?.map(tier => ({...tier, rewards: rewards?.filter(reward => reward?.loyalty_program_tier_id === tier?.id)}));
        ReactDOM.unstable_batchedUpdates(() => {
            setState(prev => ({...prev, tiers: updatedTiers}));
            setTiersBeforeState(updatedTiers);
        });
    }

    function handleToggleStatus(index: number) {
        if (state?.tiers) {
            const updatedTiers = [...state.tiers]?.map((tier, offerIndex) => offerIndex === index ? {...tier, active: tier?.active === 0 ? 1 : 0} : tier);
            setState(prev => ({...prev, tiers: updatedTiers}));
        }
    }

    function unsavedChangesExist() {
        if (tiersBeforeState === null) {
          if (state?.tiers) {
            setTiersBeforeState(state?.tiers);
          }
          return false;
        }
    
        return !isEqualWith(tiersBeforeState, state?.tiers, (originalValue, newValue) => {
          if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
            return true;
          }
        });
      }

      async function handleAddTier(tier: Partial<ITier>) {        
        const res = await PostLoyaltyProgramStep3({tiers: [tier]}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating tier")));
            return;
        }
        dispatch(showSuccess(t("Successfully updated tier")));
        //refresh tiers
        await getTiers();
        void closeTierModal();
    }

    async function handleSaveTiers() {
        const res = await PostLoyaltyProgramStep3({tiers: state?.tiers}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating tiers")));
            return;
        }
        dispatch(showSuccess(t("Successfully updated tiers")));
        //refresh tiers
        await getTiers();
    }

    async function handleDeleteOffer() {
        if (deleteModal?.tier) {
            const res = await DeleteLoyaltyProgramTier({id: deleteModal?.tier?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error deleting tier")));
                return;
            }
            dispatch(showSuccess(t("Successfully deleted tier")));
            //refresh tier
            await getTiers();
            void closeDeleteModal();
        }
    }

    async function handleDuplicateOffer() {
        if (duplicateModal?.tier) {
            const res = await PostDuplicateLoyaltyProgramTier({id: duplicateModal?.tier?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error duplicating tier")));
                return;
            }
            dispatch(showSuccess(t("Successfully duplicated tier")));
            //refresh tier
            await getTiers();
            void closeDuplicateModal();
        }
    }

      function navigateToNextStep() {
        if (state?.tiers?.length === 0) {
            dispatch(showError(t("At least one tier required before continuing to the next step")));
            return;
        }
        history.push("/admin/settings/loyalty-program");
    }

    return (
        <Page
        narrow
        title={t("Step 2: Set-up Tiers for your Program")}
        breadcrumbs={[
            {
              prefix: true,
              label: t("Back"),
              url: "/admin/settings/loyalty-program",
            },
          ]}
          notificationBarProps={{
            isVisible: unsavedChangesExist(),
            onAction: () => handleSaveTiers(),
            onCancel: () => setState(prev => ({...prev, tiers: tiersBeforeState})),
          }}
        >
            <Card title={t("Tiers")} subtitle={t("Manage tiers and its rewards to motivate your customers")} titleActions={[{content: "+ Add Tier", buttonType: "secondary", action: () => updateTierModal({isOpen: true})}]}>
                <Card.Section>
                    <br />
                    <div className="loyalty-data-table">
                    <DataTable
                    columns={[{ label: t("How to Redeem Points") }, { label: "", width: "40%" }, { label: t("Rewards") }, { label: t("Status") }, { label: "" }]}
                    className="min-h-auto"
                >
                    {state?.tiers?.map((tier, index) => {
                    return (
                        <tr key={index}>
                            <td>
                                <p style={{color: "#101828"}}>{tier?.title}</p>
                                <p>{tier?.points_required} {t("Points Required")}</p>
                            </td>
                            <td></td>
                            <td>{tier?.rewards?.length}</td>
                            <td>
                                <Toggle checked={!!tier?.active} onChange={() => handleToggleStatus(index)}/>
                            </td>

                            <td>
                            <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                          sections={[
                            [
                              {
                                label: t("Edit"),
                                icon: "pen-to-square",
                                onClick: () => updateTierModal({isOpen: true, tier: tier}),
                              },
                              {
                                label: t("Duplicate"),
                                icon: "copy",
                                onClick: () => updateDuplicateModal({isOpen: true, tier: tier}),
                              },
                              {
                                label: t("Delete"),
                                icon: "trash-can",
                                onClick: () => updateDeleteModal({isOpen: true, tier: tier}),
                              },
                            ],
                          ]}
                        />
                            </td>
                        </tr>
                    );
                })}
                    </DataTable>
                    </div>
                </Card.Section>
            </Card>
            <div className="loyalty-button-container">
            <Button type="primary" size="medium" onClick={() => navigateToNextStep()}>{t("Done")}</Button>
            </div>

            <LoyaltyProgramTiersModal open={tierModal?.isOpen} onCancel={closeTierModal} onOk={tier => handleAddTier(tier)} tier={tierModal?.tier} />
            <Popup title={t("Delete Tier")} type="warning" description={`${t("Are you sure you want to delete tier")}: ${deleteModal?.tier?.title}?`} open={deleteModal?.isOpen} onCancel={closeDeleteModal} onOk={handleDeleteOffer} okText={t("Delete")} />
            <Popup title={t("Duplicate Tier")} type="info" description={`${t("Are you sure you want to duplicate tier")}: ${duplicateModal?.tier?.title}?`} open={duplicateModal?.isOpen} onCancel={closeDuplicateModal} onOk={handleDuplicateOffer} okText={t("Duplicate")} />
        </Page>
    );
}